// Client-side mirror of validateRegistration in api/_lib/innerCircle.mjs.
// Error keys match the server so server-returned errors land on the same
// fields. The server stays authoritative.
import { normalizeInstagram } from "../../utils/instagramHandle";
import { dialCodeFor } from "../../data/dialCodes";
import { ALLOW_BOTH_ROLE } from "./content";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const hasLetter = (value) => /\p{L}/u.test(value);

export const isValidPhone = (countryId, raw) => {
  const cc = dialCodeFor(countryId).replace(/\D/g, "");
  let national = String(raw || "").replace(/\D/g, "");
  if (cc === "1") {
    if (national.length === 11 && national.startsWith("1")) national = national.slice(1);
    return national.length === 10;
  }
  if (national.startsWith("0")) national = national.slice(1);
  return national.length >= 6 && cc.length + national.length <= 15;
};

export const needsProperties = (role) => role === "host" || role === "both";

export function validateForm(form) {
  const errors = {};
  if (!form.firstName.trim() || !hasLetter(form.firstName)) errors.firstName = "Enter your first name.";
  if (!form.lastName.trim() || !hasLetter(form.lastName)) errors.lastName = "Enter your last name.";

  const roles = ALLOW_BOTH_ROLE ? ["host", "creator", "both"] : ["host", "creator"];
  if (!roles.includes(form.role)) errors.role = "Choose whether you are a host or a creator.";

  if (needsProperties(form.role)) {
    if (!form.properties.length) errors.properties = "Add at least one property.";
    form.properties.forEach((p, i) => {
      if (!p.name.trim()) errors[`properties.${i}.name`] = "Enter the property name.";
      if (!p.city.trim()) errors[`properties.${i}.city`] = "Enter the city.";
      if (!p.country.trim()) errors[`properties.${i}.country`] = "Enter the country.";
    });
  }

  if (!form.primaryInstagram.trim()) errors.primaryInstagram = "Enter your Instagram handle.";
  else if (!normalizeInstagram(form.primaryInstagram)) {
    errors.primaryInstagram = "Enter a valid Instagram handle, like @yourhandle or instagram.com/yourhandle.";
  }

  if (form.hasAdditionalHandles === "yes") {
    if (!form.additionalInstagram.length) {
      errors.additionalInstagram = "Add at least one additional handle, or choose No.";
    }
    form.additionalInstagram.forEach((h, i) => {
      if (!h.value.trim()) errors[`additionalInstagram.${i}`] = "Enter a handle or remove this row.";
      else if (!normalizeInstagram(h.value)) errors[`additionalInstagram.${i}`] = "Enter a valid Instagram handle.";
    });
  } else if (form.hasAdditionalHandles !== "no") {
    errors.hasAdditionalHandles = "Choose Yes or No.";
  }

  if (!EMAIL_REGEX.test(form.email.trim())) errors.email = "Enter a valid email address.";
  if (!isValidPhone(form.phoneCountry, form.phoneNumber)) {
    errors.phoneNumber = "Enter a valid cell number for the selected country code.";
  }

  if (!form.referralSource) errors.referralSource = "Tell us how you heard about Inner Circle.";
  if (form.referralSource === "other" && !form.referralDetail.trim()) errors.referralDetail = "Tell us a little more.";

  return errors;
}

// Order fields appear in, for focusing the first invalid one.
export function orderedErrorKeys(form, errors) {
  const keys = ["firstName", "lastName", "role"];
  if (needsProperties(form.role)) {
    keys.push("properties");
    form.properties.forEach((_, i) => keys.push(`properties.${i}.name`, `properties.${i}.city`, `properties.${i}.country`));
  }
  keys.push("primaryInstagram", "hasAdditionalHandles", "additionalInstagram");
  form.additionalInstagram.forEach((_, i) => keys.push(`additionalInstagram.${i}`));
  keys.push("email", "phoneNumber", "referralSource", "referralDetail");
  return keys.filter((k) => errors[k]);
}

export const fieldId = (key) => `ic-${key.replace(/\./g, "-")}`;
